/**
 * `lx.chat_history` row → SPA `ChatMessage` mapping, extracted from
 * `useChatSession`'s initial-load path so every consumer of the store's rows —
 * the initial hydration, back-scroll pagination pages and the tail poll
 * (agent-bus P6 item 2, `lexide-agent-bus-p6-spa-miniplan.md`) — renders a row
 * IDENTICALLY. Three inline copies of this mapping would drift; the mapper is
 * the contract.
 */
import type {
  ChatMessage,
  LxChatHistoryParams,
  LxConversationMessage,
} from "@/types/gateway";
import type { ContentBlock } from "@/types/content-blocks";
import type { LxSessionContext } from "@/types/session";
import { redactSystemTrailEntry } from "@/lib/chat/tool-activity";
import { parseReferencedFilesFromHistory } from "@/lib/workspace/mentions-directive";

/** The scope half of `LxChatHistoryParams` for a parsed session context —
 *  the one construction the initial load, back-scroll pages, the tail poll
 *  and attachment fetch-binding all share. Case scope sends ids (never a raw
 *  session key) so the server re-derives the key and the case-bound JWT
 *  validates it. */
export function lxScopeParams(
  ctx: Pick<LxSessionContext, "type" | "clientId" | "matterId" | "caseId">,
): LxChatHistoryParams {
  return ctx.type === "global"
    ? { scope: "global" }
    : { clientId: ctx.clientId, matterId: ctx.matterId, caseId: ctx.caseId };
}

/**
 * Identity of a persisted/rendered chat row for dedupe purposes.
 *
 * `(runId, role)` — a turn is one user row + one assistant row sharing the
 * runId, so the pair is unique per row while surviving every representation
 * change (live-final vs history-mapped vs optimistic local). Timestamps are
 * deliberately NOT part of the key: the optimistic local row carries a client
 * clock and the persisted row a server clock, and treating that skew as
 * identity would duplicate the row it exists to dedupe.
 */
export function chatMessageKey(msg: Pick<ChatMessage, "role" | "runId">): string {
  return `${msg.runId ?? ""}:${msg.role}`;
}

/**
 * Map one persisted conversation row to the render shape.
 *
 * `scopeParams` are the SAME scope params the history call was made with —
 * attachments bind their lazy `lx.chat_read_media` fetch to them, so a row can
 * only fetch media under the conversation it came from.
 */
export function mapLxHistoryRow(
  m: LxConversationMessage,
  scopeParams: LxChatHistoryParams,
): ChatMessage {
  // Reload echo for @-mention references: the runtime folds the
  // `<user_referenced_files>` directive into the persisted turn text
  // (render-time `stripUserPreamble` hides it from display), so we re-derive
  // the chips from it. Only user turns carry it.
  const mentions = m.role === "user" ? parseReferencedFilesFromHistory(m.text) : null;
  const histContent: ContentBlock[] =
    m.role === "assistant" && m.planTool
      ? [
          {
            type: "tool_use",
            id: `hist-${m.runId}`,
            name: m.planTool.name,
            input: m.planTool.input,
          },
          { type: "text", text: m.text },
        ]
      : [{ type: "text", text: m.text }];
  // ── reload parity (L2 read half) ──────────────────────────────────────────
  // 🚨 SWITCH ON `v`, never on field presence (absence-as-signal produced three
  // misdiagnoses in this store on 2026-08-02/03). v>=2 rows render EXCLUSIVELY
  // from `toolTrail`; `tools[]` is a legacy marker from a different source that
  // can disagree and is never cross-validated against it.
  const isTrailRow = (m.v ?? 1) >= 2;
  return {
    role: m.role,
    content: histContent,
    // Guard a malformed ts — a RangeError here would reject the whole load and
    // blank the reload over one bad row.
    timestamp: Number.isFinite(m.ts)
      ? new Date(m.ts).toISOString()
      : new Date().toISOString(),
    runId: m.runId,
    // kind rows (no_answer + cancelled): positive-value switch — only values
    // this build KNOWS pass through; a future kind falls back to today's
    // render rather than a blank bubble. `cause` rides no_answer only.
    ...(m.kind === "no_answer" || m.kind === "cancelled" ? { kind: m.kind } : {}),
    ...(m.kind === "no_answer" && m.cause ? { cause: m.cause } : {}),
    // System-tier entries are redacted at INGEST, same as the live path — the
    // target never reaches `TrailRow` (lexide-system-tier-path-gate-miniplan §3).
    ...(isTrailRow && m.toolTrail?.length
      ? { toolTrail: m.toolTrail.map(redactSystemTrailEntry) }
      : {}),
    // Spread only when OBSERVED — a v:2 row without it renders no duration at
    // all, never "0 s".
    ...(isTrailRow && typeof m.workedMs === "number" ? { workedMs: m.workedMs } : {}),
    // B2 discriminator, carried through so a runtime-injected receipt still
    // renders as a system event after a RELOAD (`lib/chat-receipt.ts`).
    ...(m.model ? { model: m.model } : {}),
    ...(m.provider ? { provider: m.provider } : {}),
    // Durable selection echo (server-persisted structured selection).
    ...(m.context?.kind === "selection"
      ? { selection: { source: m.context.sourceFile, text: m.context.text } }
      : {}),
    ...(mentions ? { mentions } : {}),
    // Conversation attachments — fetch params pre-bound to THIS scope.
    ...(m.attachments?.length
      ? {
          attachments: m.attachments.map((a) => ({
            name: a.name,
            ...(a.mime ? { mime: a.mime } : {}),
            fetch: { ...scopeParams, name: a.name },
          })),
        }
      : {}),
  };
}

/** Map + filter a page of rows (drops non-user/assistant roles verbatim like
 *  the initial-load path always has). Order is preserved (oldest→newest). */
export function mapLxHistoryRows(
  rows: LxConversationMessage[],
  scopeParams: LxChatHistoryParams,
): ChatMessage[] {
  return rows
    .filter((m) => m.role === "user" || m.role === "assistant")
    .map((m) => mapLxHistoryRow(m, scopeParams));
}
